import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import { withStyles } from '@material-ui/core/styles';
import { FormHelperText } from '@material-ui/core';

import Chip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Chip';
// eslint-disable-next-line import/no-named-default
import { default as t } from 'Theme';
import Checkbox from 'Components/ComponentLibrary/Components/Uncategorized/Checkbox';
import { createBaseStyles } from './styles';

const MUI_COLORS = [
  'initial',
  'inherit',
  'primary',
  'secondary',
  'textPrimary',
  'textSecondary',
  'error',
];

export const COLORS = [
  ...MUI_COLORS,
  'accentBlue',
  'accentBlue2',
  'accentGreen',
  'accentGreen2',
  'accentPurple',
  'lightGray',
  'primaryGray',
];

const getFilledVariantStyle = (theme, color) => {
  // This important is here because createBaseStyles applies styling to
  // the select root with very high specificity. This is not a general case solution, so
  // please do not copy it.
  return {
    backgroundColor: `${theme.palette.facet[color]} !important`,
    color: `${theme.palette.common.white} !important`,
    fontWeight: 400,
  };
};

const styles = theme => ({
  root: {
    ...createBaseStyles(theme),
  },
  inputLabel: {},
  selectRoot: {
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  selectIcon: {},
  menuItem: {},
  helperText: {},
  groupHeader: {},
  groupedOption: {
    paddingLeft: theme.spacing(3),
  },
  variantFilled: {
    padding: theme.spacing(1),
    borderRadius: '8px',
  },
  variantFilledIcon: {
    color: theme.palette.common.white,
  },
  variantFilledPrimaryBlue: getFilledVariantStyle(theme, 'primaryBlue'),
  variantFilledAccentGreen: getFilledVariantStyle(theme, 'accentGreen'),
  variantFilledAccentGreen2: getFilledVariantStyle(theme, 'accentGreen2'),
  variantFilledAccentGreen3: getFilledVariantStyle(theme, 'accentGreen3'),
  variantFilledAccentGreen4: getFilledVariantStyle(theme, 'accentGreen4'),
  variantFilledAccentGreen5: getFilledVariantStyle(theme, 'accentGreen5'),
  variantFilledAccentGreen7: getFilledVariantStyle(theme, 'accentGreen7'),
  variantFilledAccentPurple: getFilledVariantStyle(theme, 'accentPurple'),
  variantFilledAccentBlue: getFilledVariantStyle(theme, 'accentBlue'),
  variantFilledAccentBlue2: getFilledVariantStyle(theme, 'accentBlue2'),
  variantFilledLightGray: getFilledVariantStyle(theme, 'lightGray'),
  variantFilledPrimaryGray: getFilledVariantStyle(theme, 'primaryGray'),
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    color: 'white',
    margin: theme.spacing(0.25),
  },
  checkbox: {
    margin: '0px',
  },
});

function MultiselectControl({
  className,
  classes,
  error,
  helperText,
  label,
  options,
  onChange,
  selectId,
  value,
  variant,
  shouldDisableOption,
  color,
  LabelProps,
  SelectProps,
  ...rest
}) {
  let selectLabelProps = {};

  if (label) {
    selectLabelProps = {
      label,
      labelId: `${selectId}-label`,
    };
  }

  let opts = [];
  const groups = {};

  for (let i = 0; i < options.length; i++) {
    const option = options[i];

    if (option.group) {
      if (!groups[option.group]) {
        groups[option.group] = [{ groupHeader: true, label: option.group }];
      }

      groups[option.group].push(option);
    }
  }

  const groupKeys = Object.keys(groups);

  if (groupKeys.length > 0) {
    opts = groupKeys.reduce((acc, groupKey) => [...acc, ...groups[groupKey]], []);
  } else {
    opts = options;
  }

  return (
    <FormControl
      error={error}
      className={classNames(classes.root, className)}
      variant={variant}
      {...rest}
    >
      {label && (
        <InputLabel
          className={classes.inputLabel}
          htmlFor={selectId}
          id={`${selectId}-label`}
          {...LabelProps}
        >
          {label}
        </InputLabel>
      )}
      <Select
        classes={{
          root: classNames(classes.selectRoot, {
            [classes.variantFilled]: variant === 'filled',
            [classes.variantFilledPrimaryBlue]: variant === 'filled' && color === 'primaryBlue',
            [classes.variantFilledAccentGreen]: variant === 'filled' && color === 'accentGreen',
            [classes.variantFilledAccentGreen2]: variant === 'filled' && color === 'accentGreen2',
            [classes.variantFilledAccentGreen3]: variant === 'filled' && color === 'accentGreen3',
            [classes.variantFilledAccentGreen4]: variant === 'filled' && color === 'accentGreen4',
            [classes.variantFilledAccentGreen5]: variant === 'filled' && color === 'accentGreen5',
            [classes.variantFilledAccentGreen7]: variant === 'filled' && color === 'accentGreen7',
            [classes.variantFilledAccentPurple]: variant === 'filled' && color === 'accentPurple',
            [classes.variantFilledAccentBlue]: variant === 'filled' && color === 'accentBlue',
            [classes.variantFilledAccentBlue2]: variant === 'filled' && color === 'accentBlue2',
            [classes.variantFilledLightGray]: variant === 'filled' && color === 'lightGray',
            [classes.variantFilledPrimaryGray]: variant === 'filled' && color === 'primaryGray',
          }),
          icon: classNames(classes.selectIcon, {
            [classes.variantFilledIcon]: variant === 'filled',
          }),
        }}
        id={selectId}
        value={value}
        onChange={onChange}
        renderValue={(selected) => (
          <div className={classes.chips}>
            {(selected).map((s) => {
              if (opts.find(o => o.id === `${s}`)) {
                return (
                  <Chip
                    key={s}
                    label={opts.find(o => o.id === `${s}`).prompt}
                    className={classes.chip}
                    style={{ backgroundColor: t.palette.facet.accentBlue2 }}
                  />
                );
              }
              return null;
            })}
          </div>
        )}
        {...selectLabelProps}
        {...SelectProps}
      >
        {opts.map((option, i) => {
          if (option.groupHeader) {
            return (
              <MenuItem
                className={classes.groupHeader}
                color="primary"
                disabled
                key={`header.${i}`} // eslint-disable-line react/no-array-index-key
              >
                {option.label}
              </MenuItem>
            );
          }
          return (
            <MenuItem
              className={classNames(classes.menuItem, {
                [classes.groupedOption]: !!option.group,
              })}
              key={option.value}
              value={option.value}
              disabled={shouldDisableOption(option.value)}
            >
              <Checkbox
                checked={value.indexOf(option.value) > -1}
                className={classes.checkbox}
              />
              {option.name}
            </MenuItem>
          );
        })}
      </Select>
      {helperText && (
        <FormHelperText className={classes.helperText}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

MultiselectControl.defaultProps = {
  className: '',
  label: null,
  variant: 'standard',
  shouldDisableOption: () => false,
  color: 'inherit',
};

MultiselectControl.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  selectId: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  label: PropTypes.string,
  shouldDisableOption: PropTypes.func,
  color: PropTypes.oneOf(COLORS),
};

export default withStyles(styles, { name: 'MultiselectControl' })(MultiselectControl);
